import React, { useCallback, useState } from "react";
import { Button, MessageBanner } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQueryClient } from "react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { createDuplicateReview, revokeDuplicateReview, } from "../../../api/titleMergeService";
import { useDuplicateGroups } from "../../../hooks/useDuplicateGroups";
import { formatDate } from "../../../utils/formatUtils";
import css from "./MergeDuplicateTitles.module.css";
var groupKey = function (group) {
    return "".concat(group.identifierKind, "-").concat(group.identifierValue);
};
var mergeRequestOf = function (group) {
    var _a;
    var provisionals = group.members.filter(function (member) { return member.provisional; });
    var provisional = provisionals.length === 1 ? provisionals[0] : undefined;
    return {
        titleIds: group.members.map(function (member) { return member.id; }),
        titleType: group.titleType,
        provisionalId: provisional === null || provisional === void 0 ? void 0 : provisional.id,
        provisionalName: (_a = provisional === null || provisional === void 0 ? void 0 : provisional.mainName) !== null && _a !== void 0 ? _a : undefined,
    };
};
export var DuplicateTitlesBanner = function (_a) {
    var titleId = _a.titleId, onOpenMerge = _a.onOpenMerge;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useDuplicateGroups(titleId), groups = _b.groups, isGroupsError = _b.isGroupsError;
    var _c = useState(null), errorMessage = _c[0], setErrorMessage = _c[1];
    var refresh = useCallback(function () {
        queryClient.invalidateQueries(["title-duplicate-groups"]);
    }, [queryClient]);
    var reviewMutation = useMutation(function (group) {
        return createDuplicateReview(kyInstance, {
            titleType: group.titleType,
            identifierKind: group.identifierKind,
            identifierValue: group.identifierValue,
            memberTitleIds: group.members.map(function (member) { return member.id; }),
        });
    }, {
        onSuccess: refresh,
        onError: function () {
            return setErrorMessage(formatMessage({
                id: "ui-br-legaldeposits.titleMerge.duplicates.reviewFailed",
            }));
        },
    });
    var revokeMutation = useMutation(function (reviewId) { return revokeDuplicateReview(kyInstance, reviewId); }, {
        onSuccess: refresh,
        onError: function () {
            return setErrorMessage(formatMessage({
                id: "ui-br-legaldeposits.titleMerge.duplicates.revokeFailed",
            }));
        },
    });
    if (isGroupsError) {
        return (React.createElement(MessageBanner, { type: "error", "data-test-id": "duplicate-banner-failed" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.loadFailed" })));
    }
    if (groups.length === 0)
        return null;
    return (React.createElement("div", { "data-test-id": "duplicate-banner" },
        errorMessage && (React.createElement(MessageBanner, { type: "error" }, errorMessage)),
        groups.map(function (group) {
            var drifted = !group.reviewed &&
                !!group.review &&
                (group.addedCount > 0 || group.removedCount > 0);
            if (group.reviewed && group.review) {
                return (React.createElement("div", { key: groupKey(group), className: css.reviewedNote },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.reviewed", values: {
                            identifier: group.identifierValue,
                            count: group.members.length,
                            date: formatDate(group.review.reviewedAt),
                            user: group.review.reviewedByName,
                        } }),
                    React.createElement("button", { type: "button", className: css.linkButton, onClick: function () {
                            setErrorMessage(null);
                            revokeMutation.mutate(group.review.id);
                        }, disabled: revokeMutation.isLoading, "data-test-id": "duplicate-banner-revoke" },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.revoke" }))));
            }
            return (React.createElement(MessageBanner, { key: groupKey(group), type: "warning" },
                drifted && group.review ? (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.drifted", values: {
                        identifier: group.identifierValue,
                        date: formatDate(group.review.reviewedAt),
                        added: group.addedCount,
                        removed: group.removedCount,
                    } })) : (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.possible", values: {
                        identifier: group.identifierValue,
                        count: Math.max(group.members.length - 1, 0),
                    } })),
                React.createElement("div", { className: css.bannerActions },
                    React.createElement(Button, { marginBottom0: true, onClick: function () { return onOpenMerge(mergeRequestOf(group)); }, "data-test-id": "duplicate-banner-open" },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.duplicates.open" })),
                    React.createElement(Button, { marginBottom0: true, disabled: reviewMutation.isLoading, onClick: function () {
                            setErrorMessage(null);
                            reviewMutation.mutate(group);
                        }, "data-test-id": "duplicate-banner-review" },
                        React.createElement(FormattedMessage, { id: drifted
                                ? "ui-br-legaldeposits.titleMerge.duplicates.reviewAgain"
                                : "ui-br-legaldeposits.titleMerge.duplicates.markDifferent" })))));
        })));
};
